import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import axios from '../../axiosInstance';
import './NewsArticle.css';
import NavBar from '../NavBar/NavBar';
import Footer from '../Footer/Footer';
import AdsBanner from '../AdsBanner/AdsBanner';
import RunningHeader from '../RunningHeader/RunningHeader';
import RelatedArticles from './RelatedArticles';
import AdsBanner_news_mid from '../AdsBanner/AdsBanner_news_mid';
import {
  FaWhatsapp,
  FaFacebook,
  FaTwitter,
  FaShareAlt,
} from 'react-icons/fa';

const NewsArticle = () => {
  const { newsId } = useParams();
  const [newsData, setNewsData] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const hasFetched = useRef(false);
  const BASE_URL = process.env.REACT_APP_API_URL || 'https://todaylocal.in/api';

  // ✅ Ensure AdSense script runs once per render
  useEffect(() => {
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch (e) {
      console.error('AdSense error:', e);
    }
  }, []);

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;

    const fetchNews = async () => {
      try {
        setLoading(true);
        const response = await axios.get(`/api/news/read/${newsId}`);
        setNewsData(response.data);

        const relatedResponse = await axios.get(`/api/news/related/${newsId}`);
        setRelatedArticles(relatedResponse.data);

        incrementViewCount(newsId);
      } catch (err) {
        console.error('Error fetching news:', err);
        setError(err.response?.data?.error || 'Failed to load news.');
      } finally {
        setLoading(false);
      }
    };

    fetchNews();
  }, [newsId]);

  const incrementViewCount = async (newsId) => {
    const lastVisit = localStorage.getItem(`lastVisit_${newsId}`);
    const currentTime = Date.now();

    if (!lastVisit || currentTime - lastVisit >= 180000) {
      try {
        await axios.post(`/api/news/incrementViewCount`, { newsId });
        localStorage.setItem(`lastVisit_${newsId}`, currentTime);
      } catch (error) {
        console.error('Failed to increment view count', error);
      }
    }
  };

  const handleWebShare = async () => {
    if (!navigator.share) {
      alert('Web Share API not supported on this device.');
      return;
    }

    if (!newsData) {
      alert('No article data available to share.');
      return;
    }

    let imageUrl = newsData.image;

    if (imageUrl && imageUrl.startsWith("data:image/")) {
      try {
        const byteCharacters = atob(imageUrl.split(',')[1]);
        const byteNumbers = new Uint8Array([...byteCharacters].map((char) => char.charCodeAt(0)));
        const blob = new Blob([byteNumbers], { type: 'image/png' });
        imageUrl = URL.createObjectURL(blob);
      } catch (error) {
        console.error('Error processing base64 image:', error);
        alert('Image could not be processed for sharing.');
        return;
      }
    }

    try {
      await navigator.share({
        title: newsData.title,
        text: newsData.shortDescription,
        url: `https://todaylocal.in/news/${newsId}`,
      });
      console.log('Successfully shared!');
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleShareClick = (platform) => {
    const shareUrl = `https://todaylocal.in/news/${newsId}`;
    const text = `${newsData.title} - ${newsData.shortDescription}`;
    const encodedText = encodeURIComponent(text);
    const encodedUrl = encodeURIComponent(shareUrl);

    switch (platform) {
      case 'whatsapp':
        window.open(`https://wa.me/?text=${encodedText}%20${encodedUrl}`);
        break;
      case 'facebook':
        window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedText}`);
        break;
      case 'twitter':
        window.open(`https://twitter.com/intent/tweet?text=${encodedText}&url=${encodedUrl}`);
        break;
      default:
        console.error('Unsupported platform:', platform);
    }
  };

  if (loading) return <div className="loading-message">Loading...</div>;
  if (error) return <div className="error-message">{error}</div>;
  if (!newsData) return <div className="no-article-message">No article found!</div>;

  const paragraphs = newsData.description
    ? newsData.description.split(/\r?\n/).map((paragraph, index) => (
        <p
          key={index}
          dangerouslySetInnerHTML={{
            __html: paragraph.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>"),
          }}
        ></p>
      ))
    : [<p key="no-content">No content available for this article.</p>];

  return (
    <div>
      <NavBar />
      <RunningHeader />
      <AdsBanner placement="Header" page="News" />

      <div className="news-article">
        <div className="news-image">
          <img
            src={`${BASE_URL}/${newsData.image}`}
            alt="News"
          />

          {/* ✅ Google AdSense Ad Below Image */}
          <div className="ad-below-image" style={{ margin: '20px 0', textAlign: 'center' }}>
            <ins
              className="adsbygoogle"
              style={{ display: 'block', textAlign: 'center' }}
              data-ad-layout="in-article"
              data-ad-format="fluid"
              data-ad-client="ca-pub-7538470739170711"
              data-ad-slot="5342044367"
            ></ins>
          </div>
        </div>

        <div className="news-content">
          <h1 className="news-title">{newsData.title || 'Untitled Article'}</h1>

          <div className="article-mid-ad">
            <AdsBanner_news_mid placement="Middle" page="News" />
          </div>

          <p className="news-description">
            {newsData.shortDescription || 'No description available.'}
          </p>

          <div className="full-content">
            {paragraphs.map((paragraph, index) => (
              <p key={index} style={{ whiteSpace: 'pre-wrap' }}>{paragraph}</p>
            ))}
          </div>

          {/* Share Buttons */}
          <div className="share-buttons">
            <h3>Share this article:</h3>
            <button onClick={handleWebShare} className="share-icon">
              <FaShareAlt size={20} /> Share
            </button>
            <button onClick={() => handleShareClick('whatsapp')} className="share-icon">
              <FaWhatsapp size={20} /> WhatsApp
            </button>
            <button onClick={() => handleShareClick('facebook')} className="share-icon">
              <FaFacebook size={20} /> Facebook
            </button>
            <button onClick={() => handleShareClick('twitter')} className="share-icon">
              <FaTwitter size={20} /> Twitter
            </button>
          </div>

          {relatedArticles.length > 0 && (
            <RelatedArticles relatedArticles={relatedArticles} />
          )}
        </div>
      </div>

      <AdsBanner placement="Footer" page="News" />
      <Footer />
    </div>
  );
};

NewsArticle.propTypes = {
  newsId: PropTypes.string,
};

export default NewsArticle;
